import React, { useState, useEffect, useContext } from "react";
import { Redirect } from "react-router-dom";
import {
  Card,
  Row,
  CardBody,
  Col,
  Button,
  Alert,
  Form,
} from "reactstrap";
import { StateContext } from "../../utils/context";
import TenantContext from "../../utils/tenantContext";
import SubscriptionsFeatureLockAlerts from "../../components/subscriptionsFeatureLockAlerts";
import { stripeSubscriptionService } from "../../jwt/_services/stripeSubscription.service";
import { isSubscriptionFeatureLocked } from "../../utils/subscriptionUtils";
import ProductFields, {
  EMPTY_PRODUCT,
  draftToPayload,
} from "./productFields";
import "./stripeSubscriptionConfig.css";

const ProductAdd = () => {
  const { currentUser } = useContext(StateContext);
  const { tenant } = useContext(TenantContext);
  const featureLocked = isSubscriptionFeatureLocked(tenant, currentUser?.sla_level);
  const [draftProduct, setDraftProduct] = useState({ ...EMPTY_PRODUCT });
  const [productCount, setProductCount] = useState(0);
  const [tenantCurrency, setTenantCurrency] = useState("eur");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [redirect, setRedirect] = useState(false);

  useEffect(() => {
    Promise.all([
      stripeSubscriptionService.listProducts(),
      stripeSubscriptionService.getConfig(),
    ])
      .then(([productsData, configData]) => {
        const items = productsData.results || productsData || [];
        setProductCount(items.length);
        setTenantCurrency(configData.currency || "eur");
      })
      .catch(() => setError("Failed to load products."))
      .finally(() => setLoading(false));
  }, []);

  const atProductLimit = productCount >= 5;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (atProductLimit) {
      setError("Maximum of 5 active products reached.");
      return;
    }
    if (!draftProduct.name || !draftProduct.amount || !draftProduct.description?.trim()) {
      setError("Product name, price, and description are required.");
      return;
    }

    setSaving(true);
    setError("");
    stripeSubscriptionService
      .createProduct({
        ...draftToPayload(draftProduct, currentUser),
        order: productCount,
      })
      .then(() => {
        setSuccess(true);
        setTimeout(() => setRedirect(true), 1000);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Failed to add product.");
      })
      .finally(() => setSaving(false));
  };

  if (redirect) {
    return <Redirect to="/products" />;
  }

  return (
    <div className="stripe-subscription-page">
      {featureLocked && (
        <SubscriptionsFeatureLockAlerts tenant={tenant} slaLevel={currentUser?.sla_level} />
      )}

      <Row>
        <Col sm={12}>
          <Card className="stripe-section-card">
            <CardBody>
              <h3 className="config-page-title mb-4">Add Product</h3>
              {atProductLimit && !loading && (
                <Alert color="warning">
                  Maximum of 5 active products reached.
                </Alert>
              )}
              <Form onSubmit={handleSubmit}>
                <ProductFields
                  draftProduct={draftProduct}
                  setDraftProduct={setDraftProduct}
                  tenantCurrency={tenantCurrency}
                  disabled={featureLocked}
                />
                <Row className="stripe-product-form-actions-row">
                  <Col lg={8}>
                    <div className="stripe-product-form-actions">
                      <Button color="success" type="submit" disabled={featureLocked || saving || loading || atProductLimit}>
                        Save
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
              <div style={{ paddingTop: "24px" }}>
                {success && (
                  <Alert color="success">Product added successfully.</Alert>
                )}
                {error && <Alert color="danger">{error}</Alert>}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProductAdd;
